import React, { useState } from 'react';
import { Link } from 'react-router';
import { Pencil, Trash2, Mail, Calendar, Key, Ticket, ChevronRight } from 'lucide-react';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import Skeleton from '../components/ui/Skeleton';
import { ErrorState } from '../components/ui/EmptyState';
import { formatDate } from '../utils/dateUtils';
import EditProfileModal from '../components/profile/EditProfileModal';
import DeleteAccountModal from '../components/profile/DeleteAccountModal';
import { useUser } from '../hooks/useAuth';
import { useInviteCodesCanCreate } from '../hooks/useInviteCodes';
import { useLanguage } from '../lang/LangContext';
import { KEYS } from '../lang/keys';
import { useCurrency } from '../context/CurrencyContext';

const Field = ({ label, icon: Icon, children }) => (
    <div className="space-y-1">
        <span className="label-base flex items-center gap-1">
            <Icon className="w-3 h-3" /> {label}
        </span>
        <p className="text-sm text-text-primary font-medium">{children}</p>
    </div>
);

const Profile = () => {
    const { data: user, isLoading: loading, error: userError, refetch: refetchUser } = useUser();
    const { data: canCreateData } = useInviteCodesCanCreate();
    const { t } = useLanguage();
    const { currency, setCurrency } = useCurrency();
    const [success, setSuccess] = useState('');
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

    const handleProfileUpdated = () => {
        setSuccess(t(KEYS.PROFILE_UPDATE_SUCCESS));
        setTimeout(() => setSuccess(''), 3000);
    };

    const getInitials = (name) => {
        if (!name) return '?';
        return name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);
    };

    if (loading) {
        return (
            <div className="max-w-lg mx-auto space-y-5">
                <div className="flex justify-between items-center">
                    <Skeleton variant="title" width="30%" />
                    <div className="flex gap-2">
                        <Skeleton variant="button" width={100} />
                        <Skeleton variant="button" width={120} />
                    </div>
                </div>
                <Card>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Skeleton variant="text" count={2} />
                        <Skeleton variant="text" count={2} />
                    </div>
                </Card>
            </div>
        );
    }

    if (userError) {
        return (
            <div className="max-w-lg mx-auto">
                <ErrorState message={userError?.message || t(KEYS.PROFILE_ERROR_LOAD)} onRetry={refetchUser} />
            </div>
        );
    }

    return (
        <div className="max-w-lg mx-auto space-y-5">
            {/* Success Message */}
            {success && (
                <div className="p-3 bg-status-paid-bg text-status-paid rounded text-sm animate-appear">
                    {success}
                </div>
            )}

            {/* Profile Card */}
            <Card className="animate-appear">
                {/* Avatar & actions header */}
                <div className="flex items-center gap-4 mb-6">
                    <div className="w-14 h-14 rounded-full bg-surface-overlay border-2 border-accent/30 flex items-center justify-center text-lg font-semibold text-accent shrink-0">
                        {getInitials(user?.name)}
                    </div>
                    <div className="flex-1 min-w-0">
                        <h1 className="text-lg font-semibold text-text-primary truncate">
                            {user?.name || 'N/A'}
                        </h1>
                        <p className="text-xs text-text-muted">
                            {user?.email || ''}
                        </p>
                    </div>
                </div>

                {/* Fields */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
                    <Field label={t(KEYS.PROFILE_EMAIL)} icon={Mail}>
                        {user?.email || 'N/A'}
                    </Field>
                    <Field label={t(KEYS.PROFILE_MEMBER_SINCE)} icon={Calendar}>
                        <span className="font-mono text-xs">
                            {formatDate(user?.created_at)}
                        </span>
                    </Field>
                </div>

                {/* Actions */}
                <div className="flex gap-2 pt-4 border-t border-surface-border">
                    <Button
                        variant="ghost"
                        size="small"
                        onClick={() => setIsEditModalOpen(true)}
                        className="gap-1.5"
                    >
                        <Pencil className="w-3.5 h-3.5" />
                        {t(KEYS.PROFILE_EDIT)}
                    </Button>
                    <Button
                        variant="danger"
                        size="small"
                        onClick={() => setIsDeleteModalOpen(true)}
                        className="gap-1.5"
                    >
                        <Trash2 className="w-3.5 h-3.5" />
                        {t(KEYS.PROFILE_DELETE)}
                    </Button>
                </div>
            </Card>

            {/* Modals */}
            <EditProfileModal
                isOpen={isEditModalOpen}
                onClose={() => setIsEditModalOpen(false)}
                user={user}
                onProfileUpdated={handleProfileUpdated}
            />

            <DeleteAccountModal
                isOpen={isDeleteModalOpen}
                onClose={() => setIsDeleteModalOpen(false)}
                userName={user?.name}
            />

            {/* Preferences Card */}
            <Card className="animate-appear">
                <h2 className="text-xs font-semibold text-text-muted uppercase tracking-widest mb-3">
                    {t(KEYS.PROFILE_PREFERENCES)}
                </h2>
                <div className="space-y-1">
                    <label className="label-base" htmlFor="currency-input">
                        {t(KEYS.PROFILE_CURRENCY)}
                    </label>
                    <input
                        id="currency-input"
                        type="text"
                        className="input-base w-32"
                        value={currency}
                        onChange={(e) => setCurrency(e.target.value)}
                    />
                </div>
            </Card>

            {/* Integrations Card */}
            <Card className="animate-appear">
                <h2 className="text-xs font-semibold text-text-muted uppercase tracking-widest mb-3">
                    {t(KEYS.PROFILE_INTEGRATIONS)}
                </h2>
                <Link
                    to="/api-keys"
                    className="flex items-center gap-3 p-3 rounded-md hover:bg-surface-overlay transition-colors group"
                >
                    <div className="w-8 h-8 rounded-md bg-surface-overlay border border-surface-border flex items-center justify-center shrink-0 group-hover:border-accent/40 transition-colors">
                        <Key className="w-4 h-4 text-text-muted group-hover:text-accent transition-colors" />
                    </div>
                    <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-text-primary">{t(KEYS.PROFILE_API_KEYS_LABEL)}</p>
                        <p className="text-xs text-text-muted">{t(KEYS.PROFILE_API_KEYS_DESCRIPTION)}</p>
                    </div>
                    <ChevronRight className="w-4 h-4 text-text-muted group-hover:text-accent transition-colors shrink-0" />
                </Link>
                {canCreateData?.can_create && (
                    <Link
                        to="/invite-codes"
                        className="flex items-center gap-3 p-3 rounded-md hover:bg-surface-overlay transition-colors group"
                    >
                        <div className="w-8 h-8 rounded-md bg-surface-overlay border border-surface-border flex items-center justify-center shrink-0 group-hover:border-accent/40 transition-colors">
                            <Ticket className="w-4 h-4 text-text-muted group-hover:text-accent transition-colors" />
                        </div>
                        <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-text-primary">{t(KEYS.PROFILE_INVITE_CODES_LABEL)}</p>
                            <p className="text-xs text-text-muted">{t(KEYS.PROFILE_INVITE_CODES_DESCRIPTION)}</p>
                        </div>
                        <ChevronRight className="w-4 h-4 text-text-muted group-hover:text-accent transition-colors shrink-0" />
                    </Link>
                )}
            </Card>
        </div>
    );
};

export default Profile;
